/**
 * Satış haritası (panel, read only, v0.1.269; Sercan, 3 Nov 2026: the old portal's "Global Satış Radarı" in Twin, and
 * better): GET /sales-map?period=&category=. Etsy orders that count as revenue (not cancelled or refunded) by country
 * and by the buyer's town, placed with the API's offline gazetteer; the totals of the period against the one before;
 * the newest orders and the months in which new countries were reached. Istanbul days. Places are towns at the finest:
 * no buyer name, e-mail, street line or postal code is stored or sent (ADR 0018).
 */
import { z } from 'zod';
import { countSchema, isoDateSchema, isoDateTimeSchema } from '../ids.js';
/** all: every order Twin has; 365, 90, 30: the last that many Istanbul days, today included. */
export const SALES_MAP_PERIODS = ['all', '365', '90', '30'];
export const SALES_MAP_DEFAULT_PERIOD = 'all';
/** Product families as the catalog sells them: scans and 3D models, STEP CAD, DIY plans, packs (bundles). */
export const SALES_CATEGORIES = ['scan', 'step', 'plan', 'pack'];
export const SALES_MAP_CATEGORY_FILTERS = ['all', ...SALES_CATEGORIES];
/** A town's dominant family; mixed when no family has 60 % of its revenue. */
export const SALES_DOMINANT = [...SALES_CATEGORIES, 'mixed'];
/**
 * How a town was placed: city (the gazetteer knows the town), region (the centre of its state or province), country
 * (the country's centre) or none (the country has no centre either; the panel uses the shape it draws).
 */
export const SALES_PLACE_PRECISIONS = ['city', 'region', 'country', 'none'];
/** At most this many recent orders, countries' top products and series months are sent. */
export const SALES_MAP_RECENT_LIMIT = 25;
export const SALES_MAP_TOP_PRODUCTS = 3;
export const salesMapQuerySchema = z.strictObject({
    period: z.enum(SALES_MAP_PERIODS).optional(),
    category: z.enum(SALES_MAP_CATEGORY_FILTERS).optional(),
});
const iso2Schema = z.string().regex(/^[A-Z]{2}$/);
const latSchema = z.number().min(-90).max(90);
const lonSchema = z.number().min(-180).max(180);
export const salesMapTotalsSchema = z.strictObject({
    countries: countSchema,
    cities: countSchema,
    orders: countSchema,
    revenueCents: countSchema,
});
/** The running Istanbul month: countries and towns with their first order this month, orders and revenue. */
export const salesMapThisMonthSchema = z.strictObject({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    newCountries: countSchema,
    newCities: countSchema,
    orders: countSchema,
    revenueCents: countSchema,
});
export const salesMapRangeSchema = z.strictObject({ from: isoDateSchema, to: isoDateSchema });
export const salesMapProductSchema = z.strictObject({
    title: z.string().min(1).max(300),
    category: z.enum(SALES_CATEGORIES),
    orders: countSchema,
    revenueCents: countSchema,
});
export const salesMapCountrySchema = z.strictObject({
    iso2: iso2Schema,
    /** Turkish country name. */
    name: z.string().min(1).max(80),
    orders: countSchema,
    revenueCents: countSchema,
    cities: countSchema,
    /** First and last order day in the chosen family, over all time (first) and the period (last). */
    firstDay: isoDateSchema,
    lastDay: isoDateSchema,
    topProducts: z.array(salesMapProductSchema).max(SALES_MAP_TOP_PRODUCTS),
});
export const salesMapCitySchema = z.strictObject({
    /** Stable key: "CC|region|city" folded. */
    key: z.string().min(3).max(260),
    country: iso2Schema,
    region: z.string().max(120).nullable(),
    /** Null: the receipts of these orders named no town. */
    city: z.string().max(120).nullable(),
    lat: latSchema.nullable(),
    lon: lonSchema.nullable(),
    precision: z.enum(SALES_PLACE_PRECISIONS),
    /** True only when the town itself was found (precision city). */
    located: z.boolean(),
    orders: countSchema,
    revenueCents: countSchema,
    dominant: z.enum(SALES_DOMINANT),
    lastDay: isoDateSchema,
});
export const salesMapOrderSchema = z.strictObject({
    day: isoDateSchema,
    country: iso2Schema.nullable(),
    region: z.string().max(120).nullable(),
    city: z.string().max(120).nullable(),
    /** The first item's title; "+N" counts the others in itemCount. */
    title: z.string().min(1).max(300),
    itemCount: countSchema,
    category: z.enum(SALES_CATEGORIES),
    totalCents: countSchema,
});
export const salesMapMonthSchema = z.strictObject({
    month: z.string().regex(/^\d{4}-\d{2}$/),
    orders: countSchema,
    revenueCents: countSchema,
    /** Countries with their first order (in the chosen family) this month, and all reached up to its end. */
    newCountries: countSchema,
    countriesTotal: countSchema,
    /** ISO codes of the new countries. */
    newCountryCodes: z.array(iso2Schema),
});
export const salesMapViewSchema = z.strictObject({
    generatedAt: isoDateTimeSchema,
    timeZone: z.string().min(1).max(64),
    today: isoDateSchema,
    period: z.enum(SALES_MAP_PERIODS),
    category: z.enum(SALES_MAP_CATEGORY_FILTERS),
    /** The period's days; null for all (from the first order). */
    range: salesMapRangeSchema.nullable(),
    /** The same number of days right before range; null for all. */
    previousRange: salesMapRangeSchema.nullable(),
    totals: salesMapTotalsSchema,
    previous: salesMapTotalsSchema.nullable(),
    thisMonth: salesMapThisMonthSchema,
    /** Most revenue first. */
    countries: z.array(salesMapCountrySchema),
    /** Most orders first. */
    cities: z.array(salesMapCitySchema),
    /** Newest first. */
    recentOrders: z.array(salesMapOrderSchema).max(SALES_MAP_RECENT_LIMIT),
    /** Oldest first, from the first order's month to the running one. */
    months: z.array(salesMapMonthSchema),
    /** Orders of the period whose town the gazetteer did not find (placed at a region or country centre). */
    unlocatedOrders: countSchema,
    /** Orders of the period whose receipt named no town (read before v0.1.269 and not re-read yet, or none on it). */
    noCityOrders: countSchema,
});
//# sourceMappingURL=sales-map.js.map